import React, { useMemo } from "react";
import { colors } from "../../styles/colors";
import { coerceNotes, NoteEntry } from "../notes/noteTypes";
import { formatMoney } from "../notes/MilestoneNotes";

const INCOME_COLOR = "#16a34a";
const EXPENSE_COLOR = "#ef4444";

interface FinanceReportProps {
  goals: any[];
  milestonesByGoal: Record<string, any[]>;
  locked?: boolean;
  onUpgrade?: () => void;
}

/** Sample data shown (blurred) to free users so they get a glance of the feature. */
const MOCK_GOALS = [
  { id: "mg1", title: "Down payment fund" },
  { id: "mg2", title: "Home renovation" },
];
const MOCK_MILESTONES: Record<string, any[]> = {
  mg1: [
    { id: "mm1", title: "Open savings account", notes: [{ id: "mn1", type: "finance", createdAt: "2026-01-15T00:00:00Z", direction: "income", amount: 2500, currency: "USD", content: "Salary top-up" }] },
    { id: "mm2", title: "Monthly transfer", notes: [{ id: "mn2", type: "finance", createdAt: "2026-02-10T00:00:00Z", direction: "income", amount: 2000, currency: "USD", content: "Auto-save" }] },
  ],
  mg2: [
    { id: "mm3", title: "Buy materials", notes: [{ id: "mn3", type: "finance", createdAt: "2026-02-20T00:00:00Z", direction: "expense", amount: 1800, currency: "USD", content: "Tiles & paint" }] },
    { id: "mm4", title: "Hire contractor", notes: [{ id: "mn4", type: "finance", createdAt: "2026-03-05T00:00:00Z", direction: "expense", amount: 1400, currency: "USD", content: "Labour deposit" }] },
  ],
};

interface FinanceLine {
  goalId: string;
  goalTitle: string;
  milestoneId: string;
  milestoneTitle: string;
  note: NoteEntry;
}

interface CurrencyTotals {
  currency: string;
  income: number;
  expense: number;
}

const IconFinance = ({ c = colors.textTertiary, size = 16 }: { c?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const fmtDate = (iso?: string): string => {
  if (!iso) return "";
  // Date-only strings (YYYY-MM-DD) are anchored to local midnight to avoid TZ shifts.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const FinanceReport: React.FC<FinanceReportProps> = ({ goals, milestonesByGoal, locked = false, onUpgrade }) => {
  const srcGoals = locked ? MOCK_GOALS : goals;
  const srcMilestones = locked ? MOCK_MILESTONES : milestonesByGoal;

  const lines = useMemo<FinanceLine[]>(() => {
    const out: FinanceLine[] = [];
    for (const goal of srcGoals || []) {
      const milestones = srcMilestones[goal.id] || [];
      for (const m of milestones) {
        const notes = coerceNotes(m.notes);
        for (const note of notes) {
          if (note.type === "finance" && typeof note.amount === "number") {
            out.push({
              goalId: goal.id,
              goalTitle: goal.title || "Untitled goal",
              milestoneId: m.id,
              milestoneTitle: m.title || "Untitled milestone",
              note,
            });
          }
        }
      }
    }
    return out;
  }, [srcGoals, srcMilestones]);

  const totalsByCurrency = useMemo<CurrencyTotals[]>(() => {
    const map = new Map<string, CurrencyTotals>();
    for (const l of lines) {
      const cur = l.note.currency || "USD";
      if (!map.has(cur)) map.set(cur, { currency: cur, income: 0, expense: 0 });
      const t = map.get(cur)!;
      if (l.note.direction === "income") t.income += l.note.amount || 0;
      else t.expense += l.note.amount || 0;
    }
    return Array.from(map.values()).sort((a, b) => a.currency.localeCompare(b.currency));
  }, [lines]);

  const linesByGoal = useMemo(() => {
    const map = new Map<string, { goalTitle: string; items: FinanceLine[] }>();
    for (const l of lines) {
      if (!map.has(l.goalId)) map.set(l.goalId, { goalTitle: l.goalTitle, items: [] });
      map.get(l.goalId)!.items.push(l);
    }
    // Keep goal order from source
    const ordered: { goalId: string; goalTitle: string; items: FinanceLine[] }[] = [];
    for (const goal of srcGoals || []) {
      const entry = map.get(goal.id);
      if (entry) ordered.push({ goalId: goal.id, goalTitle: entry.goalTitle, items: entry.items });
    }
    return ordered;
  }, [lines, srcGoals]);

  if (!locked && lines.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: "48px 24px",
          border: "1px dashed rgba(0,0,0,0.12)",
          borderRadius: 16,
          background: "#fff",
          textAlign: "center",
        }}
      >
        <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(0,136,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <IconFinance c={colors.brandPrimary} size={24} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>No finances recorded yet</div>
        <div style={{ fontSize: 13, color: colors.textTertiary, maxWidth: 360, lineHeight: 1.6 }}>
          Add a <strong>Finance</strong> note to any milestone to record an expense or income. They'll be consolidated here as a report for the whole dream.
        </div>
      </div>
    );
  }

  const cardBase: React.CSSProperties = {
    flex: 1,
    minWidth: 160,
    border: "1px solid rgba(0,0,0,0.07)",
    borderRadius: 14,
    background: "#fff",
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  };

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Per-currency summary */}
      {totalsByCurrency.map((t) => {
        const net = t.income - t.expense;
        return (
          <div key={t.currency} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: colors.textPrimary, letterSpacing: 0.3 }}>{t.currency}</span>
              <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.06)" }} />
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={cardBase}>
                <span style={{ fontSize: 11, fontWeight: 700, color: colors.textTertiary, textTransform: "uppercase", letterSpacing: 0.4 }}>Income</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: INCOME_COLOR }}>{formatMoney(t.income, t.currency)}</span>
              </div>
              <div style={cardBase}>
                <span style={{ fontSize: 11, fontWeight: 700, color: colors.textTertiary, textTransform: "uppercase", letterSpacing: 0.4 }}>Expense</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: EXPENSE_COLOR }}>{formatMoney(t.expense, t.currency)}</span>
              </div>
              <div style={{ ...cardBase, background: net >= 0 ? "rgba(22,163,74,0.06)" : "rgba(239,68,68,0.06)", border: `1px solid ${net >= 0 ? "rgba(22,163,74,0.25)" : "rgba(239,68,68,0.25)"}` }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: colors.textTertiary, textTransform: "uppercase", letterSpacing: 0.4 }}>Net</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: net >= 0 ? INCOME_COLOR : EXPENSE_COLOR }}>
                  {net < 0 ? "\u2212" : ""}{formatMoney(Math.abs(net), t.currency)}
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Breakdown by goal */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: colors.textPrimary }}>Transactions</div>
        {linesByGoal.map((group) => (
          <div key={group.goalId} style={{ border: "1px solid rgba(0,0,0,0.07)", borderRadius: 14, background: "#fff", overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", background: "#f8fafc", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
              {group.goalTitle}
            </div>
            <div>
              {group.items
                .slice()
                .sort((a, b) => (b.note.date || b.note.createdAt || "").localeCompare(a.note.date || a.note.createdAt || ""))
                .map((l, i) => (
                  <div
                    key={l.note.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "10px 14px",
                      borderTop: i === 0 ? "none" : "1px solid rgba(0,0,0,0.05)",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                      <span style={{ fontSize: 13, color: colors.textPrimary, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {l.milestoneTitle}
                      </span>
                      <span style={{ fontSize: 11, color: colors.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {l.note.content ? `${l.note.content} · ` : ""}{fmtDate(l.note.date || l.note.createdAt)}
                      </span>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 800, whiteSpace: "nowrap", color: l.note.direction === "income" ? INCOME_COLOR : EXPENSE_COLOR }}>
                      {l.note.direction === "income" ? "+" : "\u2212"}{formatMoney(l.note.amount, l.note.currency)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (!locked) return content;

  return (
    <div style={{ position: "relative" }}>
      <div aria-hidden style={{ filter: "blur(6px)", pointerEvents: "none", userSelect: "none", opacity: 0.65 }}>
        {content}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          background: "rgba(255,255,255,0.35)",
        }}
      >
        <div
          style={{
            maxWidth: 380,
            width: "100%",
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 16,
            boxShadow: "0 12px 40px rgba(0,0,0,0.16)",
            padding: "24px 22px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
          }}
        >
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(0,136,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <IconFinance c={colors.brandPrimary} size={24} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: colors.textPrimary }}>Finance is a Premium feature</div>
          <div style={{ fontSize: 13, color: colors.textTertiary, lineHeight: 1.6 }}>
            Log expenses and income on any milestone and see a consolidated report for your whole dream. Upgrade to unlock it.
          </div>
          <button
            type="button"
            onClick={onUpgrade}
            style={{
              marginTop: 4,
              border: "none",
              background: colors.brandPrimary,
              color: "#fff",
              borderRadius: 10,
              padding: "10px 22px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Upgrade to Premium
          </button>
        </div>
      </div>
    </div>
  );
};
